/**
 * DEV-101: вызовы сервиса авторизации osago-accounts.
 *
 * Отдельно от apiHelpers намеренно: там базовый URL основного бэкенда и своя обёртка,
 * а здесь другой хост и ручной fetch, чтобы не получить цикл импортов
 * (apiHelpers -> accountsAuth -> apiHelpers).
 *
 * Вход пока только по email и паролю. Телефон в сервисе готов и выключен флагом, так что
 * добавление следующего способа - это ещё одна функция рядом, без перетряски транспорта.
 */
import { ACCOUNTS_API_URL } from '../config/api';

import { clearTokens, getAccessToken, getRefreshToken, setTokens } from './tokenStore';

interface TokenPair {
  access: string;
  refresh: string;
}

/** Сервис отвечает тем же конвертом, что основной бэкенд: { data, error, message } */
interface Envelope<T> {
  data: T | null;
  error: boolean;
  message: string;
}

export function isAccountsConfigured(): boolean {
  return Boolean(ACCOUNTS_API_URL);
}

async function post<T>(path: string, body: unknown, token?: string): Promise<Envelope<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${ACCOUNTS_API_URL}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  return (await res.json()) as Envelope<T>;
}

export async function loginWithPassword(email: string, password: string): Promise<string | null> {
  const envelope = await post<TokenPair>('/auth/login', { email, password });
  if (envelope.error || !envelope.data) return envelope.message || 'Не удалось войти';
  setTokens(envelope.data.access, envelope.data.refresh);
  return null;
}

export interface AccountsProfile {
  fullName: string;
  email: string;
}

/** GET /auth/me: принципал плюс профиль. Профиля нет у гостевого токена. */
export async function fetchProfile(): Promise<AccountsProfile | null> {
  const token = await ensureAccessToken();
  if (!token) return null;

  const res = await fetch(`${ACCOUNTS_API_URL}/auth/me`, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  const envelope = (await res.json()) as Envelope<{ profile?: AccountsProfile }>;
  if (envelope.error || !envelope.data?.profile) return null;
  return {
    fullName: envelope.data.profile.fullName || '',
    email: envelope.data.profile.email || '',
  };
}

export async function requestPasswordReset(email: string): Promise<void> {
  // Ответ намеренно одинаковый для существующего и несуществующего адреса,
  // поэтому и обрабатывать тут нечего: наличие аккаунта не раскрываем.
  await post('/auth/password/reset-request', { email });
}

/** Завершение сброса: токен из письма плюс новый пароль. Строка - текст ошибки. */
export async function confirmPasswordReset(
  token: string,
  newPassword: string,
): Promise<string | null> {
  try {
    const envelope = await post('/auth/password/reset', { token, newPassword });
    if (envelope.error) return envelope.message || 'Ссылка устарела, запросите новую';
    return null;
  } catch {
    return 'Сервис недоступен, попробуйте позже';
  }
}
export async function logout(): Promise<void> {
  const access = getAccessToken();
  const refresh = getRefreshToken();
  try {
    if (access) await post('/auth/logout', { refresh }, access);
  } catch {
    // сеть отвалилась - локальные токены всё равно чистим ниже
  }
  clearTokens();
}

// Обновление в один поток: при загрузке страницы несколько запросов стартуют разом,
// и без этого каждый дёрнул бы refresh. Refresh одноразовый, и сервис на повторное
// использование убивает всю семью токенов - то есть разлогинивает человека.
let inFlight: Promise<string | null> | null = null;

async function doRefresh(): Promise<string | null> {
  const refresh = getRefreshToken();
  if (!refresh) return null;
  try {
    const envelope = await post<TokenPair>('/auth/refresh', { refresh });
    if (envelope.error || !envelope.data) {
      clearTokens();
      return null;
    }
    setTokens(envelope.data.access, envelope.data.refresh);
    return envelope.data.access;
  } catch {
    // Сервис недоступен - токены НЕ чистим: это не отказ в доступе, а временный сбой,
    // и после восстановления человек продолжит работать.
    return null;
  }
}

/**
 * Access есть - отдаём его. Нет, но есть refresh - обновляемся.
 * Ничего нет - null, и транспорт просто не добавит заголовок.
 */
export async function ensureAccessToken(): Promise<string | null> {
  if (!isAccountsConfigured()) return null;
  const access = getAccessToken();
  if (access) return access;
  if (!getRefreshToken()) return null;

  if (!inFlight) {
    inFlight = doRefresh().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}
